import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, UserPlus, Trash2, AlertCircle, X, KeyRound, Copy, CheckCircle2, Edit2 } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

export default function TeamManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this employee account?")) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <div data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">Director only</div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">Team</h1>
          <p className="text-[#4A595E] mt-2">Add, edit and remove employee accounts.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-user-btn">
          <UserPlus className="w-4 h-4" /> Add employee
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : (
        <div className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F1E4D5] text-left">
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0E5DE]">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-[#F1E4D5]/50 transition">
                  <td className="px-5 py-4 font-medium text-[#1F2A2E]">{u.name}</td>
                  <td className="px-5 py-4 text-sm text-[#284952]">{u.email}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full" style={{ background: u.role === "admin" ? "#FCE7EB" : "#DDE7E9", color: u.role === "admin" ? "#A12545" : "#284952" }}>
                      {u.role === "admin" && <ShieldCheck className="w-3 h-3" />} {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs">
                    <span className={u.active ? "text-emerald-700" : "text-[#6B7A80]"}>{u.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setEditing(u)}
                        className="text-[#284952] hover:text-[#E2718A] transition"
                        data-testid={`edit-user-${u.id}`}
                        aria-label="Edit profile"
                        title="Edit profile"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setResetting(u)}
                        className="text-[#284952] hover:text-[#E2718A] transition"
                        data-testid={`reset-password-${u.id}`}
                        aria-label="Reset password"
                        title="Reset password"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      {u.id !== user.id && (
                        <button onClick={() => deleteUser(u.id)} className="text-red-500 hover:text-red-700 transition" data-testid={`delete-user-${u.id}`} aria-label="Delete user" title="Delete user">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <UserFormModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => setResetting(null)} />}
      {editing && (
        <EditUserModal
          user={editing}
          isSelf={editing.id === user.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "staff",
    active: user.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API}/users/${user.id}`, form);
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-user-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F0E5DE]">
          <div>
            <h2 className="font-display text-2xl text-[#1F2A2E]">Edit profile</h2>
            <div className="text-xs text-[#6B7A80] mt-0.5">{user.email}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="edit-user-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Full name</label>
            <input
              className="input-navica"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="edit-user-name"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Email address</label>
            <input
              type="email"
              className="input-navica"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              data-testid="edit-user-email"
            />
            <p className="text-xs text-[#6B7A80] mt-1.5">This is also their login.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Role</label>
            <select
              className="input-navica"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              disabled={isSelf}
              data-testid="edit-user-role"
            >
              <option value="staff">Staff (sees only assigned clients)</option>
              <option value="admin">Admin (sees everything)</option>
            </select>
            {isSelf && <p className="text-xs text-[#6B7A80] mt-1.5">You can&apos;t change your own role.</p>}
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl bg-[#F1E4D5] border border-[#F0E5DE]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              disabled={isSelf}
              className="navica-check mt-0.5"
              data-testid="edit-user-active"
            />
            <span className="text-sm text-[#284952]">
              <span className="font-semibold">Account active</span>
              <span className="block text-xs text-[#6B7A80] mt-0.5">Inactive accounts can&apos;t log in.{isSelf && " You can\u2019t deactivate yourself."}</span>
            </span>
          </label>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="edit-user-submit">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserFormModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/users`, form);
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't create user.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="user-form-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F0E5DE]">
          <h2 className="font-display text-2xl text-[#1F2A2E]">Add employee</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Full name</label>
            <input className="input-navica" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Email</label>
            <input type="email" className="input-navica" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Temporary password</label>
            <input type="text" className="input-navica" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} placeholder="Min 8 characters" data-testid="user-password" />
            <p className="text-xs text-[#6B7A80] mt-1.5">Share this securely with the employee. They can change it later.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Role</label>
            <select className="input-navica" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
              <option value="staff">Staff (sees only assigned clients)</option>
              <option value="admin">Admin (sees everything)</option>
            </select>
          </div>
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="user-submit">
              {saving ? "Creating..." : "Create employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function Th({ children }) {
  return <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function generatePassword() {
  // 12-char password with upper/lower/digit/symbol, friendly to type but secure enough
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // no I/O
  const lower = "abcdefghijkmnpqrstuvwxyz"; // no l/o
  const digit = "23456789"; // no 0/1
  const sym = "!@#$%&*";
  const all = upper + lower + digit + sym;
  const arr = new Uint32Array(12);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(arr);
  else for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 0xffffffff);
  let pw = "";
  pw += upper[arr[0] % upper.length];
  pw += lower[arr[1] % lower.length];
  pw += digit[arr[2] % digit.length];
  pw += sym[arr[3] % sym.length];
  for (let i = 4; i < 12; i++) pw += all[arr[i] % all.length];
  return pw.split("").sort(() => 0.5 - Math.random()).join("");
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [password, setPassword] = useState(() => generatePassword());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const regen = () => {
    setPassword(generatePassword());
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore, user can manually select
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API}/users/${user.id}/password`, { new_password: password });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't reset password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="reset-password-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F0E5DE]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#FCE7EB] text-[#E2718A] flex items-center justify-center">
              <KeyRound className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-display text-xl text-[#1F2A2E] leading-tight">Reset password</h2>
              <div className="text-xs text-[#6B7A80]">For {user.name} ({user.email})</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="reset-password-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="reset-password-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h3 className="font-display text-xl text-[#1F2A2E] mb-2">Password reset</h3>
            <p className="text-sm text-[#284952] mb-5">
              Share the new password with {user.name} securely (not via plain email). They can log in straight away.
            </p>
            <div className="bg-[#F1E4D5] rounded-2xl p-4 mb-5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">New password</div>
              <div className="flex items-center justify-between gap-3">
                <code className="font-mono text-lg text-[#1F2A2E] break-all">{password}</code>
                <button onClick={copy} className="text-[#E2718A] hover:text-[#284952] transition shrink-0" aria-label="Copy" data-testid="reset-password-copy-done">
                  {copied ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button onClick={onDone} className="btn-primary w-full" data-testid="reset-password-done">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">New password</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="input-navica font-mono flex-1"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={copy} className="btn-outline !px-3 !py-2" aria-label="Copy password" data-testid="reset-password-copy">
                  {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-[#6B7A80]">Min 8 characters. We&apos;ve generated a strong one for you.</p>
                <button type="button" onClick={regen} className="text-xs text-[#E2718A] hover:underline font-semibold" data-testid="reset-password-regen">
                  Generate new
                </button>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-[#F1E4D5] border border-[#F0E5DE] text-xs text-[#284952] flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 text-[#EA823A] shrink-0 mt-0.5" />
              <span>This will replace {user.name}&apos;s current password immediately and clear any active lockouts. Share the new password securely (1Password, Signal, in person).</span>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
              <button type="submit" disabled={saving || password.length < 8} className="btn-primary" data-testid="reset-password-submit">
                {saving ? "Resetting..." : "Reset password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
